import React, { useState, useEffect, useCallback } from 'react';
import { paymentApi, invoiceApi, projectApi } from '../services/api';
import { Card, Select, Spinner, Badge } from '../components/UI';
import { CurrencyDollar, Receipt, CreditCard, Bank, Money } from '@phosphor-icons/react';

import { fmtCurrency } from '../utils/currencyFormat';

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('');

  useEffect(() => {
    Promise.all([projectApi.list(), invoiceApi.list()])
      .then(([p, inv]) => { setProjects(p.data); setInvoices(inv.data); })
      .finally(() => setLoading(false));
  }, []);

  const loadPayments = useCallback(async () => {
    const res = await paymentApi.list();
    let list = res.data;
    if (filterProject) {
      list = list.filter(p => p.project_id === filterProject);
    }
    setPayments(list);
  }, [filterProject]);

  useEffect(() => { loadPayments(); }, [loadPayments]);

  const fmt = (n) => fmtCurrency(n);

  const methodIcon = (m) => {
    switch (m) {
      case 'cash': return <Money className="w-4 h-4 text-[#00C853]" />;
      case 'check': return <Receipt className="w-4 h-4 text-[#FFAB00]" />;
      case 'online': return <CreditCard className="w-4 h-4 text-[#002FA7]" />;
      default: return <Bank className="w-4 h-4 text-[#666]" />;
    }
  };

  const totalPayments = payments.reduce((s, p) => s + (p.amount || 0), 0);

  const byMethod = {};
  payments.forEach(p => { byMethod[p.payment_method] = (byMethod[p.payment_method] || 0) + (p.amount || 0); });

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="payments-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Payments</h1>
          <p className="text-sm text-[#666]">All recorded payments across invoices</p>
        </div>
        <div className="bg-[#00C853]/10 text-[#00C853] px-4 py-2 rounded-lg text-sm font-bold" data-testid="total-payments">
          Total Received: {fmt(totalPayments)}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <Select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="w-64" data-testid="filter-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
      </div>

      {/* Method breakdown */}
      {Object.keys(byMethod).length > 0 && (
        <div className="flex flex-wrap gap-3" data-testid="method-breakdown">
          {Object.entries(byMethod).map(([m, v]) => (
            <div key={m} className="bg-white border border-[#E0E0E0] rounded-lg px-3 py-1.5 text-xs flex items-center gap-2">
              {methodIcon(m)}
              <span className="capitalize text-[#666]">{m.replace('_', ' ')}: </span>
              <span className="font-bold text-[#0A0A0A]">{fmt(v)}</span>
            </div>
          ))}
        </div>
      )}

      {payments.length === 0 ? (
        <Card className="p-8 text-center text-[#666]">
          <CurrencyDollar className="w-10 h-10 mx-auto mb-3 text-[#E0E0E0]" />
          <p>No payments recorded yet. Record payments from the Invoices page.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {payments.map(p => (
            <Card key={p.id} className="p-3 sm:p-4" data-testid={`payment-row-${p.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {methodIcon(p.payment_method)}
                    <span className="font-semibold text-sm text-[#0A0A0A]">
                      Invoice: {p.invoice_number || p.invoice_id}
                    </span>
                    <Badge variant="success">{p.payment_method?.replace('_', ' ')}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-[#666]">
                    <span>{p.payment_date}</span>
                    {p.reference && <span>Ref: {p.reference}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-bold text-sm text-[#00C853]" data-testid={`payment-amount-${p.id}`}>
                    {fmt(p.amount)}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
